/*
 * Формы заявок сайта: демо и партнёрские.
 *
 * Демо-заявка ничего не ждёт в модалке — биллинг отвечает сразу, а страница
 * прогресса показывает, как создаётся аккаунт. Кнопка остаётся заблокированной,
 * пока биллинг не подтвердит, что email свободен.
 */

import { billing, requestJson } from "./billing.js";

// Биллинг ключует заявки по региону, а не по коду страны.
const REGION_BY_DIAL_CODE = {
    "+992": 1,
    "+998": 2,
    "+7": 3,
    "+996": 4,
};

const PHONE_LENGTH_BY_DIAL_CODE = {
    "+992": 9,
    "+998": 9,
    "+7": 10,
    "+996": 9,
    "+1": 10,
};

// Длинные коды первыми: иначе «7» съест «998».
const DIAL_CODES = Object.keys(PHONE_LENGTH_BY_DIAL_CODE)
    .map((dial) => ({
        dial,
        digits: dial.replace(/\D/g, ""),
        localLen: PHONE_LENGTH_BY_DIAL_CODE[dial],
    }))
    .sort((a, b) => b.digits.length - a.digits.length);

function digitsOnly(value) {
    return String(value || "").replace(/\D/g, "");
}

/*
 * Автозаполнение браузера кладёт в поле полный номер (+99890...).
 * Селект кода страны уже выбран — вырезаем код, чтобы не склеить +998998...
 */
function splitPhone(raw, selectedDial = "+998") {
    const digits = digitsOnly(raw);
    const selected =
        DIAL_CODES.find((item) => item.dial === selectedDial) || DIAL_CODES[0];

    if (!digits) {
        return { dial: selected.dial, local: "" };
    }

    const candidates = [selected, ...DIAL_CODES.filter((item) => item.dial !== selected.dial)];

    for (const code of candidates) {
        if (
            digits.startsWith(code.digits) &&
            digits.length >= code.digits.length + code.localLen
        ) {
            return { dial: code.dial, local: digits.slice(code.digits.length) };
        }
    }

    return { dial: selected.dial, local: digits };
}

function fullPhone(raw, selectedDial) {
    const parsed = splitPhone(raw, selectedDial);

    return parsed.dial + parsed.local;
}

function applyDial(form, dial) {
    form.querySelector("[data-dial-picker]")?.__applyDial?.(dial);

    const hidden = form.querySelector("[data-lead-dial-code]");
    if (hidden) hidden.value = dial;
}

function stripAutofilledCountryCode(form) {
    const input = form.querySelector("[name=phone]");
    if (!input) return;

    const selected = form.querySelector("[data-lead-dial-code]")?.value || "+998";
    const orig = digitsOnly(input.value);
    const parsed = splitPhone(input.value, selected);

    if (!parsed.local || parsed.local === orig) return;

    input.value = parsed.local;
    applyDial(form, parsed.dial);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_DEBOUNCE_MS = 400;
const TURNSTILE_WAIT_MS = 1500;

const PARTNER_STORAGE_KEY = "shamcrm.partner_id";
export const DEMO_STARTED_KEY = "shamcrm.demo_started";

/* ------------------------------------------------------------------ */
/* Хранилища                                                           */
/* ------------------------------------------------------------------ */

function readStorage(storage, key) {
    try {
        return storage.getItem(key);
    } catch (error) {
        return null;
    }
}

function writeStorage(storage, key, value) {
    try {
        storage.setItem(key, value);
    } catch (error) {
        // Приватный режим блокирует запись — на текущей странице всё работает.
    }
}

function partnerIdFromUrl() {
    // Ссылки вида ?partner_id=7#form встречаются часто, поэтому хеш срезаем.
    const rawSearch = window.location.href.split("?")[1]?.split("#")[0] || "";
    const value = new URLSearchParams(rawSearch).get("partner_id");

    return value && /^\d+$/.test(value) && Number(value) > 0 ? value : null;
}

export function persistPartnerFromUrl() {
    const fromUrl = partnerIdFromUrl();
    if (fromUrl) writeStorage(sessionStorage, PARTNER_STORAGE_KEY, fromUrl);
}

function readPartnerId() {
    persistPartnerFromUrl();

    const stored = partnerIdFromUrl() || readStorage(sessionStorage, PARTNER_STORAGE_KEY);

    return stored && /^\d+$/.test(stored) ? Number(stored) : null;
}

export function hasStartedDemo() {
    return readStorage(localStorage, DEMO_STARTED_KEY) === "1";
}

function markDemoStarted() {
    writeStorage(localStorage, DEMO_STARTED_KEY, "1");
}

/* ------------------------------------------------------------------ */
/* Поля и ошибки                                                       */
/* ------------------------------------------------------------------ */

function isDemoLead(form) {
    return (form.dataset.leadType || "demo") === "demo";
}

function isCallbackLead(form) {
    return (form.dataset.leadType || "") === "callback";
}

function setFieldError(form, name, message) {
    const holder = form.querySelector(`[data-error-for="${name}"]`);
    if (holder) holder.textContent = message || "";
}

function clearErrors(form) {
    form.querySelectorAll("[data-error-for]").forEach((el) => {
        el.textContent = "";
    });
}

function collect(form) {
    const read = (name) => form.querySelector(`[name=${name}]`)?.value.trim() || "";
    const selectedDial = form.querySelector("[data-lead-dial-code]")?.value || "+998";
    const parsed = splitPhone(read("phone"), selectedDial);

    const values = {
        fio: read("fio"),
        email: read("email"),
        organization: read("organization"),
        comment: read("comment"),
        phoneNumber: parsed.local,
        program: read("program"),
        dialCode: parsed.dial,
    };

    if (values.program) {
        values.comment = values.comment
            ? `${values.program}. ${values.comment}`
            : values.program;
    }

    return values;
}

function validate(form, values) {
    const errors = {};

    if (!values.fio) {
        errors.fio = form.dataset.msgRequiredName || "Укажите имя";
    }

    if (!isCallbackLead(form)) {
        if (!values.email) {
            errors.email = form.dataset.msgRequiredEmail || "Укажите email";
        } else if (!EMAIL_PATTERN.test(values.email)) {
            errors.email = form.dataset.msgInvalidEmail || "Проверьте адрес почты";
        } else if (isDemoLead(form) && form.dataset.emailState === "taken") {
            errors.email = form.dataset.msgEmailUnavailable || "Этот email уже используется";
        }
    }

    const digits = digitsOnly(values.phoneNumber);
    const expected = PHONE_LENGTH_BY_DIAL_CODE[values.dialCode] || 9;

    if (!digits) {
        errors.phone = form.dataset.msgRequiredPhone || "Укажите телефон";
    } else if (digits.length < expected) {
        errors.phone = (
            form.dataset.msgPhoneLength || "Номер должен содержать :count цифр"
        ).replace(":count", String(expected));
    }

    const consent = form.querySelector("[data-lead-consent]");
    if (consent && !consent.checked) {
        errors.consent = form.dataset.msgConsent || "Подтвердите согласие";
    }

    return errors;
}

/**
 * Кнопка активна, только когда форма заполнена целиком и биллинг подтвердил
 * почту. Пользователь не отправляет заявку вслепую, чтобы получить ошибку.
 */
function updateSubmitState(form) {
    if (!isDemoLead(form)) return;

    const button = form.querySelector("[type=submit]");
    if (!button || button.dataset.busy === "1") return;

    const values = collect(form);
    const complete = Object.keys(validate(form, values)).length === 0;
    // Недоступность проверки не должна запирать форму: биллинг всё равно
    // перепроверит почту при создании заявки.
    const emailOk = ["free", "unknown"].includes(form.dataset.emailState);

    button.disabled = !(complete && emailOk);
}

/* ------------------------------------------------------------------ */
/* Проверка email по биллингу                                          */
/* ------------------------------------------------------------------ */

function initEmailCheck(form) {
    const input = form.querySelector("[name=email]");
    if (!input) return;

    const spinner = form.querySelector("[data-email-spinner]");
    const okMark = form.querySelector("[data-email-ok]");
    const status = form.querySelector("[data-email-status]");

    let debounce = null;
    let controller = null;
    let requestId = 0;
    const cache = new Map();

    const render = (state, message) => {
        form.dataset.emailState = state;

        if (spinner) spinner.hidden = state !== "checking";
        if (okMark) okMark.hidden = state !== "free";

        if (status) {
            const note = state === "checking" || state === "free" || state === "unknown";
            status.hidden = !note || !message;
            status.textContent = note ? message || "" : "";
        }

        setFieldError(form, "email", state === "taken" || state === "invalid" ? message : "");
        updateSubmitState(form);
    };

    const reset = () => {
        window.clearTimeout(debounce);
        controller?.abort();
        requestId += 1;
        render("idle", "");
    };

    const check = async () => {
        const email = input.value.trim();

        if (!email) {
            reset();
            return;
        }

        if (!EMAIL_PATTERN.test(email)) {
            render("invalid", form.dataset.msgInvalidEmail || "Проверьте адрес почты");
            return;
        }

        if (cache.has(email)) {
            const cached = cache.get(email);
            render(cached.state, cached.message);
            return;
        }

        const id = ++requestId;
        controller?.abort();
        controller = new AbortController();

        render("checking", form.dataset.msgEmailChecking || "Проверяем…");

        let result;

        try {
            const { ok, data } = await requestJson(billing.emailCheck(email), {
                signal: controller.signal,
            });

            if (id !== requestId) return;

            if (!ok) {
                result = {
                    state: "unknown",
                    message: form.dataset.msgEmailCheckFailed || "",
                };
            } else if (data.available) {
                result = { state: "free", message: form.dataset.msgEmailFree || "" };
            } else {
                result = {
                    state: "taken",
                    message: data.message || form.dataset.msgEmailUnavailable || "",
                };
            }
        } catch (error) {
            if (error.name === "AbortError" || id !== requestId) return;

            result = {
                state: "unknown",
                message: form.dataset.msgEmailCheckFailed || "",
            };
        }

        // Отрицательный вердикт кешируем: он не изменится за сессию.
        if (result.state !== "unknown") cache.set(email, result);

        render(result.state, result.message);
    };

    input.addEventListener("input", () => {
        window.clearTimeout(debounce);
        render("idle", "");
        debounce = window.setTimeout(check, EMAIL_DEBOUNCE_MS);
    });

    input.addEventListener("blur", () => {
        window.clearTimeout(debounce);
        check();
    });

    form.addEventListener("lead:reset", reset);
    reset();
}

/* ------------------------------------------------------------------ */
/* Turnstile                                                           */
/* ------------------------------------------------------------------ */

/**
 * Токен капчи. Виджет в режиме interaction-only решает задачу молча и не
 * всегда успевает к первому клику, поэтому недолго ждём — но только если он
 * вообще загрузился. Биллинг пропускает заявку и без токена, так что ждать
 * впустую (например, когда скрипт Cloudflare заблокирован) нельзя.
 */
async function turnstileToken(form) {
    const container = form.querySelector("[data-turnstile]");
    if (!container || !window.turnstile) return "";

    const field = () => form.querySelector("[name=cf-turnstile-response]");
    const deadline = Date.now() + TURNSTILE_WAIT_MS;

    while (!field()?.value && Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 150));
    }

    return field()?.value || "";
}

function resetTurnstile(form) {
    const container = form.querySelector("[data-turnstile]");

    if (container && window.turnstile?.reset) {
        try {
            window.turnstile.reset(container);
        } catch (error) {
            // Виджет мог не отрендериться — заявка всё равно уйдёт.
        }
    }
}

/* ------------------------------------------------------------------ */
/* Отправка                                                           */
/* ------------------------------------------------------------------ */

function setBusy(form, busy, label) {
    const button = form.querySelector("[type=submit]");
    if (!button) return;

    if (busy) {
        button.dataset.originalLabel = button.dataset.originalLabel ?? button.innerHTML;
        button.dataset.busy = "1";
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        button.innerHTML = `<span class="site-spinner" aria-hidden="true"></span> ${label}`;
        return;
    }

    delete button.dataset.busy;
    button.removeAttribute("aria-busy");
    button.innerHTML = button.dataset.originalLabel ?? button.innerHTML;

    if (isDemoLead(form)) {
        updateSubmitState(form);
    } else {
        button.disabled = false;
    }
}

function trackLead(type) {
    // Ждём, пока gtag отдаст хит, и только потом уходим со страницы.
    // Иначе переход на «спасибо» или в кабинет обрывает generate_lead.
    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        window.setTimeout(finish, 1000);

        if (typeof window.__siteTrackLead === "function") {
            window.__siteTrackLead(type, finish);
            return;
        }

        if (typeof window.gtag === "function") {
            window.gtag("event", "generate_lead", {
                method: type,
                transport_type: "beacon",
                event_callback: finish,
            });
        } else {
            finish();
        }

        if (typeof window.ym === "function") {
            window.ym(101872361, "reachGoal", `lead_${type}`);
        }
    });
}

function applyServerErrors(form, data) {
    if (data.errors && typeof data.errors === "object") {
        Object.entries(data.errors).forEach(([field, messages]) => {
            const mapped =
                field === "client_phone"
                    ? "phone"
                    : field === "cf_turnstile_response"
                        ? "form"
                        : field;

            setFieldError(
                form,
                mapped,
                Array.isArray(messages) ? messages.join(" ") : String(messages)
            );
        });
        return;
    }

    setFieldError(form, "form", data.message || form.dataset.msgError || "Не удалось отправить заявку");
}

function progressUrl(form, uuid) {
    const template = form.dataset.progressUrl || "/demo/creating/__UUID__";

    return template.includes("__UUID__")
        ? template.replace("__UUID__", encodeURIComponent(uuid))
        : `${template.replace(/\/+$/, "")}/${encodeURIComponent(uuid)}`;
}

async function submitDemo(form, values) {
    const payload = {
        fio: values.fio,
        email: values.email,
        phone: fullPhone(values.phoneNumber, values.dialCode),
        region_id: REGION_BY_DIAL_CODE[values.dialCode] || null,
        manager_id: null,
        cf_turnstile_response: await turnstileToken(form),
    };

    const partnerId = readPartnerId();
    if (partnerId) payload.partner_id = partnerId;

    const { ok, status, data } = await requestJson(billing.demoRequests(), {
        method: "POST",
        body: JSON.stringify(payload),
    });

    if (!ok) {
        resetTurnstile(form);

        if (status === 429) {
            setFieldError(form, "form", form.dataset.msgThrottled || form.dataset.msgError || "");
        } else {
            applyServerErrors(form, data);
        }

        return false;
    }

    await trackLead("demo");
    markDemoStarted();

    // Аккаунт из недавней заявки уже готов — открываем его без страницы прогресса.
    if (data.login_url) {
        window.location.assign(data.login_url);
        return true;
    }

    if (data.uuid) {
        window.location.assign(progressUrl(form, data.uuid));
        return true;
    }

    setFieldError(form, "form", form.dataset.msgError || "Не удалось открыть демо");

    return false;
}


/* Заказ звонка с сайта: тот же origin, без биллинга. API сам создаёт лида
   и ставит звонок операторам. */
async function submitCallback(form, values) {
    const clientPhone = fullPhone(values.phoneNumber, values.dialCode);
    const url = form.dataset.callbackUrl || "/api/order-callback";

    const { ok, status, data } = await requestJson(url, {
        method: "POST",
        body: JSON.stringify({
            client_phone: clientPhone,
            company_website: form.querySelector("[data-honeypot]")?.value || "",
        }),
    });

    if (!ok) {
        if (status === 429) {
            setFieldError(form, "form", form.dataset.msgThrottled || form.dataset.msgError || "");
        } else {
            applyServerErrors(form, data);
        }

        return false;
    }

    await trackLead("callback");

    const thanksUrl = form.dataset.thanksUrl;
    if (thanksUrl && thanksUrl !== "#") {
        const next = new URL(thanksUrl, window.location.origin);
        next.searchParams.set("type", "callback");
        window.location.assign(next.toString());
        return true;
    }

    const successPanel = form.closest("[data-lead-scope]")?.querySelector("[data-lead-success]");
    if (successPanel) {
        form.classList.add("hidden");
        successPanel.classList.remove("hidden");
    }

    form.reset();
    return true;
}

async function submitLegacyLead(form, values) {
    const payload = {
        fio: values.fio,
        email: values.email,
        phone: fullPhone(values.phoneNumber, values.dialCode),
        request_type: form.dataset.leadType || "partner",
        region_id: REGION_BY_DIAL_CODE[values.dialCode] || null,
        manager_id: null,
        cf_turnstile_response: await turnstileToken(form),
    };

    const partnerId = readPartnerId();
    if (partnerId) payload.partner_id = partnerId;
    if (values.organization) payload.organization = values.organization;
    if (values.comment) payload.comment = values.comment;

    const { ok, status, data } = await requestJson(billing.legacyLead(), {
        method: "POST",
        body: JSON.stringify(payload),
    });

    if (!ok) {
        resetTurnstile(form);

        if (status === 429) {
            setFieldError(form, "form", form.dataset.msgThrottled || form.dataset.msgError || "");
        } else {
            applyServerErrors(form, data);
        }

        return false;
    }

    await trackLead(payload.request_type);

    const thanksUrl = form.dataset.thanksUrl;
    if (thanksUrl && thanksUrl !== "#") {
        const next = new URL(thanksUrl, window.location.origin);
        next.searchParams.set("type", payload.request_type);
        window.location.assign(next.toString());
        return true;
    }

    const successPanel = form.closest("[data-lead-scope]")?.querySelector("[data-lead-success]");
    if (successPanel) {
        form.classList.add("hidden");
        successPanel.classList.remove("hidden");
    }

    form.reset();
    resetTurnstile(form);

    return true;
}

function honeypotFilled(form) {
    return (form.querySelector("[data-honeypot]")?.value || "").trim() !== "";
}

function showQuietSuccess(form) {
    const successPanel = form.closest("[data-lead-scope]")?.querySelector("[data-lead-success]");
    if (successPanel) {
        form.classList.add("hidden");
        successPanel.classList.remove("hidden");
    }
    form.reset();
}

async function submit(form) {
    // A filled trap is a bot. Show the same success state and send nothing,
    // so the bot does not learn which field gave it away.
    //
    // Демо-форму ловушкой не проверяем. Браузерный автофилл заполняет скрытое
    // поле `company_website` вместе с именем и email, и живой человек видел
    // «Заявка принята» вместо страницы создания демо — запрос в биллинг
    // вообще не уходил. Демо и так защищено Turnstile на стороне биллинга.
    if (!isDemoLead(form) && honeypotFilled(form)) {
        showQuietSuccess(form);
        return;
    }

    stripAutofilledCountryCode(form);

    const values = collect(form);
    const isDemo = isDemoLead(form);

    clearErrors(form);

    const errors = validate(form, values);
    if (Object.keys(errors).length) {
        Object.entries(errors).forEach(([name, message]) => setFieldError(form, name, message));
        updateSubmitState(form);
        return;
    }

    setBusy(
        form,
        true,
        isDemo
            ? form.dataset.msgCreating || "Создаём демо…"
            : form.dataset.msgSending || "Отправляем…"
    );

    try {
        const redirected = isDemo
            ? await submitDemo(form, values)
            : isCallbackLead(form)
                ? await submitCallback(form, values)
                : await submitLegacyLead(form, values);

        // При переходе на другую страницу возвращать кнопку в исходное
        // состояние не нужно — иначе она мигнёт перед навигацией.
        if (redirected) return;
    } catch (error) {
        setFieldError(
            form,
            "form",
            form.dataset.msgNetwork || "Нет связи с сервером. Попробуйте ещё раз."
        );
    }

    setBusy(form, false);
}

/* ------------------------------------------------------------------ */
/* Инициализация                                                       */
/* ------------------------------------------------------------------ */

export function resetLeadForm(form) {
    clearErrors(form);
    form.dispatchEvent(new CustomEvent("lead:reset"));
}

export function initLeadForms() {
    document.querySelectorAll("[data-lead-form]").forEach((form) => {
        form.setAttribute("novalidate", "novalidate");

        const phone = form.querySelector("[name=phone]");
        if (phone) {
            const strip = () => stripAutofilledCountryCode(form);
            phone.addEventListener("input", strip);
            phone.addEventListener("change", strip);
            // Chrome часто заполняет поле без input — ловим после фокуса.
            phone.addEventListener("focus", () => {
                window.setTimeout(strip, 50);
                window.setTimeout(strip, 300);
            });
        }

        if (isDemoLead(form)) {
            initEmailCheck(form);

            form.addEventListener("input", () => updateSubmitState(form));
            form.addEventListener("change", () => updateSubmitState(form));
            updateSubmitState(form);
        }

        form.addEventListener("submit", (event) => {
            event.preventDefault();
            submit(form);
        });
    });
}
